/**
 * TurnDisplay component - Display a single conversation turn.
 *
 * Design: DENiZEN — organic, warm chat bubbles
 * - Rounded avatars and message bubbles
 * - User messages: light green
 * - AI messages: white with subtle shadow
 * - WCAG 2.1 AA compliant
 */

import ReactMarkdown from 'react-markdown'
import type { ConversationTurn } from '@/models'

export interface TurnDisplayProps {
  turn: ConversationTurn
  showTimestamp?: boolean
}

export function TurnDisplay({
  turn,
  showTimestamp = false,
}: TurnDisplayProps) {
  const isUser = turn.speaker === 'user'

  const formatTime = (timestamp: Date | string) => {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div
      className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
      role="article"
      aria-label={`${turn.speaker} message`}
    >
      {/* Message bubble */}
      <div
        className={`max-w-[75%] px-4 py-3 overflow-hidden ${
          isUser
            ? 'chat-bubble-user'
            : 'chat-bubble-assistant'
        }`}
      >
        {/* Voice indicator */}
        {isUser && turn.input_mode === 'voice' && (
          <div className="flex items-center gap-1.5 mb-2 text-xs text-dark-green/50 font-body">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z"
                clipRule="evenodd"
              />
            </svg>
            <span>Voice</span>
          </div>
        )}

        {/* Message content */}
        {isUser ? (
          <p className="text-[15px] leading-relaxed whitespace-pre-wrap break-words text-dark-green font-body">
            {turn.content}
          </p>
        ) : (
          <div className="text-[15px] leading-relaxed break-words text-dark-green font-body [overflow-wrap:anywhere] space-y-2">
            <ReactMarkdown>{turn.content.trim()}</ReactMarkdown>
          </div>
        )}

        {/* Timestamp */}
        {showTimestamp && (
          <div className="text-xs mt-2 text-muted font-body">
            {formatTime(turn.timestamp)}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * System prompt display — DENiZEN welcome
 */
export interface SystemPromptProps {
  text: string
}

export function SystemPrompt({ text }: SystemPromptProps) {
  return (
    <div className="flex justify-center py-6">
      <div className="max-w-md text-center">
        <img
          src="/images/brand/Denizen_Pebbles.png"
          alt="DENiZEN"
          className="w-32 mb-4 mx-auto"
        />
        <p className="text-dark-green text-lg leading-relaxed font-body">{text}</p>
      </div>
    </div>
  )
}

export default TurnDisplay
